import {
  formatDateTime,
  formatJson,
  getStatusColorPalette,
  normalizeAuditStatus,
} from '@/features/AuditLogs/utils/auditLogUtils'
import type { AuditLogs } from '@/generated/http-clients/v3ApiSchemas'
import {
  ERROR_TOAST_OPTION,
  SUCCESS_TOAST_OPTION,
  toaster,
} from '@/hooks/useToast/toastUtils'
import {
  Badge,
  Box,
  Code,
  HStack,
  Heading,
  IconButton,
  Stack,
  Text,
  VStack,
} from '@chakra-ui/react'
import { Clipboard as ClipboardIcon, FileText, X } from 'lucide-react'
import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'

interface AuditLogDetailPanelProps {
  log: AuditLogs | null
  onCollapse?: () => void
}

interface DetailRowProps {
  label: string
  value: ReactNode
}

function DetailRow({ label, value }: Readonly<DetailRowProps>) {
  return (
    <Box>
      <Text fontSize="xs" color="gray.500" fontWeight="medium" mb={1}>
        {label}
      </Text>
      {/* Box (div), not Text (p): `value` can itself be an element that renders
          a <p> (e.g. the user-agent Text), and <p> cannot nest inside <p>. */}
      <Box fontSize="sm">{value}</Box>
    </Box>
  )
}

interface JsonSectionProps {
  title: string
  data: unknown
  copyLabel: string
  copySuccessMessage: string
  copyErrorMessage: string
}

function JsonSection({
  title,
  data,
  copyLabel,
  copySuccessMessage,
  copyErrorMessage,
}: Readonly<JsonSectionProps>) {
  const jsonString = formatJson(data)

  const handleCopy = () => {
    if (jsonString !== '-') {
      navigator.clipboard
        .writeText(jsonString)
        .then(() => {
          toaster.create({
            ...SUCCESS_TOAST_OPTION,
            title: copySuccessMessage,
          })
        })
        .catch(() => {
          toaster.create({
            ...ERROR_TOAST_OPTION,
            title: copyErrorMessage,
          })
        })
    }
  }

  return (
    <Box>
      <HStack justify="space-between" mb={3}>
        <Heading size="sm">{title}</Heading>
        {jsonString !== '-' && (
          <IconButton
            aria-label={copyLabel}
            variant="ghost"
            size="xs"
            onClick={handleCopy}
          >
            <ClipboardIcon size={14} />
          </IconButton>
        )}
      </HStack>
      <Code
        display="block"
        whiteSpace="pre-wrap"
        p={4}
        borderRadius="md"
        fontSize="xs"
        overflow="auto"
        maxH="200px"
        bg="gray.50"
      >
        {jsonString}
      </Code>
    </Box>
  )
}

export function AuditLogDetailPanel({
  log,
  onCollapse,
}: Readonly<AuditLogDetailPanelProps>) {
  const { t } = useTranslation('features', { useSuspense: false })

  if (!log) {
    return (
      <VStack
        justify="center"
        align="center"
        h="100%"
        color="gray.400"
        gap={3}
        p={6}
      >
        <FileText size={48} strokeWidth={1} />
        <Text fontSize="sm" textAlign="center">
          {String(t('auditLogs.table.clickToViewDetails'))}
        </Text>
      </VStack>
    )
  }

  const statusText = log.status
    ? String(
        t(`auditLogs.status.${normalizeAuditStatus(log.status)}`, {
          defaultValue: log.status,
        }),
      )
    : '-'

  return (
    <Stack gap={6} py={4} pl={4} pr={0} h="100%" overflowY="auto">
      <HStack justify="space-between" borderBottomWidth="1px" pb={2} pr={4}>
        <Heading size="md">{String(t('auditLogs.drawer.title'))}</Heading>
        {onCollapse && (
          <IconButton
            aria-label="Close details panel"
            variant="ghost"
            size="sm"
            onClick={onCollapse}
          >
            <X size={18} />
          </IconButton>
        )}
      </HStack>

      <Stack gap={4}>
        <DetailRow
          label={String(t('auditLogs.drawer.timestamp'))}
          value={formatDateTime(log.timestamp)}
        />

        <DetailRow
          label={String(t('auditLogs.drawer.user'))}
          value={log.user || '-'}
        />

        <DetailRow
          label={String(t('auditLogs.drawer.resource'))}
          value={
            <Code fontSize="xs" whiteSpace="pre-wrap" display="block">
              {formatJson(log.resource)}
            </Code>
          }
        />

        <Box>
          <Text fontSize="xs" color="gray.500" fontWeight="medium" mb={1}>
            {String(t('auditLogs.drawer.status'))}
          </Text>
          <Badge colorPalette={getStatusColorPalette(log.status)} size="md">
            {statusText}
          </Badge>
        </Box>

        <DetailRow
          label={String(t('auditLogs.drawer.ipAddress'))}
          value={log.ip_address || '-'}
        />

        <DetailRow
          label={String(t('auditLogs.drawer.userAgent'))}
          value={
            <Text fontSize="sm" wordBreak="break-word">
              {log.user_agent || '-'}
            </Text>
          }
        />
      </Stack>

      <JsonSection
        title={String(t('auditLogs.drawer.requestMetadata'))}
        data={log.request_metadata}
        copyLabel={String(t('auditLogs.drawer.copyRequestMetadata'))}
        copySuccessMessage={String(t('auditLogs.drawer.copySuccess'))}
        copyErrorMessage={String(t('auditLogs.drawer.copyError'))}
      />

      <JsonSection
        title={String(t('auditLogs.drawer.responseMetadata'))}
        data={log.response_metadata}
        copyLabel={String(t('auditLogs.drawer.copyResponseMetadata'))}
        copySuccessMessage={String(t('auditLogs.drawer.copySuccess'))}
        copyErrorMessage={String(t('auditLogs.drawer.copyError'))}
      />
    </Stack>
  )
}
